import React, { useEffect, useRef, useState, useMemo } from 'react'
import axios from 'axios'
import { formatCoordinates } from '../utils'
import moment from 'moment'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import 'leaflet.heat'
import _ from 'lodash'
import { DateRangePicker } from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ComposedChart, AreaChart, Area } from 'recharts'
import '../App.css'

const API = process.env.REACT_APP_APIHOST || 'http://localhost:3033'
const COUNTRIES = ["Syria","Lebanon","Palestine","Jordan","Iraq","Yemen","Egypt","Saudi Arabia","UAE","Kuwait","Bahrain","Qatar","Oman","Turkey","Iran","Afghanistan","Pakistan","Libya","Tunisia","Algeria","Morocco","Sudan","Somalia","Israel","Ukraine","Russia","South Sudan","Myanmar"]

export default function Map({ coords, events, fetchEvents = () => {}, locked = false, dateFrom, dateTo }) {
  // Core state
  const [showEvents, setShowEvents] = useState(true)
  const [viewBounds, setViewBounds] = useState(null)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [dateRange, setDateRange] = useState([{ startDate: new Date(Date.now() - 14*86400000), endDate: new Date(), key: 'selection' }])
  // The header date filter (App.js) is the source of truth — keep the major-events filter in sync
  useEffect(() => {
    if (dateFrom && dateTo) setDateRange([{ startDate: new Date(dateFrom + 'T00:00:00'), endDate: new Date(dateTo + 'T23:59:59'), key: 'selection' }])
  }, [dateFrom, dateTo])
  const fetchEventsRef = useRef(_.debounce(fetchEvents, 1500))
  const popupRef = useRef()
  const mapRef = useRef(null)
  const focusBoundsRef = useRef(null)

  // Search
  const [searchQuery, setSearchQuery] = useState('')
  const [searchSuggestions, setSearchSuggestions] = useState([])
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [showSearchBar, setShowSearchBar] = useState(false)
  const searchTimeout = useRef(null)
  const searchRef = useRef(null)

  // Panels
  const [showGraphPanel, setShowGraphPanel] = useState(false)
  const [graphTab, setGraphTab] = useState('events')
  const [showIntelPanel, setShowIntelPanel] = useState(false)
  const [intelTab, setIntelTab] = useState('intel')
  const [showAlertsPanel, setShowAlertsPanel] = useState(false)

  // Intel data
  const [intelData, setIntelData] = useState(null)
  const [intelLoading, setIntelLoading] = useState(false)
  const [mapReports, setMapReports] = useState([])
  const [expReport, setExpReport] = useState('first')
  const [mapArsenal, setMapArsenal] = useState(null)

  // Lightbox
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 })

  // Alerts
  const [alertContacts, setAlertContacts] = useState([])
  const [showAlertForm, setShowAlertForm] = useState(false)
  const [editAlertId, setEditAlertId] = useState(null)
  const [alertForm, setAlertForm] = useState({ firstName: '', lastName: '', phone: '', telegramId: '', channels: { sms: true, telegram: true }, locations: [] })
  const [alertLocInput, setAlertLocInput] = useState('')

  // Chart data
  const chartData = useMemo(() => {
    if (!events || events.length === 0) return []
    const grouped = events.reduce((acc, event) => {
      const date = new Date(event.timestamp * 1000).toISOString().split('T')[0]
      if (!acc[date]) acc[date] = { date: new Date(date), eventsCount: 0, fatalities: 0 }
      acc[date].eventsCount += 1
      acc[date].fatalities += event.fatalities || 0
      return acc
    }, {})
    return Object.values(grouped).sort((a, b) => a.date - b.date)
  }, [events])

  const timelineData = useMemo(() => {
    if (!events) return []
    return events.map(event => ({
      id: event.id || event.timestamp,
      title: event.country,
      fatalities: event.fatalities || 0,
      subtitle: moment(new Date(event.timestamp * 1000)).format('D MMM YYYY'),
      description: event.notes,
      date: new Date(event.timestamp * 1000)
    })).sort((a, b) => b.date - a.date)
  }, [events])

  // Major-events panel is built from the SAME heatmap data as the map (coords),
  // clipped to the current view — so the list matches exactly what's on the map.
  const filteredEvents = useMemo(() => {
    const src = (coords || []).map(e => ({
      country: e[0], latitude: e[1], longitude: e[2], notes: e[3], fatalities: e[4], timestamp: e[5],
    }))
    const inView = viewBounds
      ? src.filter(e => isFinite(e.latitude) && isFinite(e.longitude) && viewBounds.contains([e.latitude, e.longitude]))
      : src
    return inView
      .filter(e => { const n = (e.notes || '').trim(); return n.length > 5 && n.toLowerCase() !== 'untitled' })
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
  }, [coords, viewBounds])

  // Map markers are drawn from the SAME filtered set as the "Major events" panel,
  // so what you see on the map is exactly what's listed on the right.
  const filteredEventsRef = useRef(filteredEvents)
  useEffect(() => { filteredEventsRef.current = filteredEvents }, [filteredEvents])
  const drawEventMarkers = () => {
    const map = mapRef.current; if (!map) return
    if (map._eventMarkers) { try { map.removeLayer(map._eventMarkers) } catch (e) {} map._eventMarkers = null }
    const fe = filteredEventsRef.current
    if (map.getZoom() > 9 && fe && fe.length) {
      const layer = L.layerGroup()
      fe.forEach((e) => {
        const lat = parseFloat(e.latitude), lng = parseFloat(e.longitude)
        if (!isFinite(lat) || !isFinite(lng)) return
        const f = +e.fatalities || 0
        let color = '#C29717'
        if (f > 10 || (e.notes && (String(e.notes).includes('detonate') || String(e.notes).includes('explos')))) color = '#e55'
        else if (f > 0) color = '#e88a2a'
        L.circleMarker([lat, lng], { radius: 6, color, weight: 1, fillColor: color, fillOpacity: 0.8 })
          .bindPopup('<strong>' + (e.country || '') + '</strong><br/>Fatalities: ' + f + '<br/>' + (e.notes || ''))
          .addTo(layer)
      })
      layer.addTo(map); map._eventMarkers = layer
    }
  }

  const formatContent = (s) => {
    if (!s) return ""
    var t = s.split("\\n").join("<br/>")
    t = t.split(String.fromCharCode(10)).join("<br/>")
    while (t.indexOf("<br/><br/><br/>") !== -1) t = t.split("<br/><br/><br/>").join("<br/><br/>")
    return t
  }

  // Fetchers
  const fetchIntel = async () => {
    fetchMapReports(); fetchMapArsenal()
    if (intelData) { setShowIntelPanel(true); return }
    setIntelLoading(true); setShowIntelPanel(true)
    try {
      const res = await axios.get(API + '/summary')
      if (res.data.status && res.data.data) setIntelData(res.data.data)
    } catch (e) { console.error(e) }
    finally { setIntelLoading(false) }
  }

  const fetchMapArsenal = async () => {
    try {
      const res = await axios.get(API + '/arsenal')
      if (res.data.status) setMapArsenal(res.data.data)
    } catch (e) {}
  }

  const fetchMapReports = async () => {
    try {
      const res = await axios.get(API + '/reports?status=Published')
      if (res.data.status) setMapReports(res.data.data)
    } catch (e) {}
  }

  const fetchAlertContacts = async () => {
    try {
      const res = await axios.get(API + '/alert-contacts')
      if (res.data.status) setAlertContacts(res.data.data)
    } catch (e) {}
  }

  const saveAlert = async () => {
    if (!alertForm.firstName || !alertForm.lastName) return
    try {
      if (editAlertId) await axios.put(API + '/alert-contacts/' + editAlertId, alertForm)
      else await axios.post(API + '/alert-contacts', alertForm)
      setShowAlertForm(false); setEditAlertId(null)
      setAlertForm({ firstName: '', lastName: '', phone: '', telegramId: '', channels: { sms: true, telegram: true }, locations: [] })
      fetchAlertContacts()
    } catch (e) {}
  }

  const deleteAlert = async (id) => { await axios.delete(API + '/alert-contacts/' + id); fetchAlertContacts() }
  const toggleAlertActive = async (item) => { await axios.put(API + '/alert-contacts/' + item._id, { active: !item.active }); fetchAlertContacts() }

  // Click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) setShowDatePicker(false)
    }
    if (showDatePicker) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showDatePicker])

  // Listen for zoom-to-countries from unified header
  useEffect(() => {
    const handler = (e) => {
      if (e.detail && e.detail.bounds) {
        focusBoundsRef.current = e.detail.bounds // remember, so a (re)built map re-centers here
        if (mapRef.current) mapRef.current.fitBounds(e.detail.bounds, { animate: true, maxZoom: 6 })
      }
    }
    const alertsHandler = () => { setShowAlertsPanel(true); fetchAlertContacts() }
    window.addEventListener('forte-zoom', handler)
    window.addEventListener('forte-alerts', alertsHandler)
    return () => { window.removeEventListener('forte-zoom', handler); window.removeEventListener('forte-alerts', alertsHandler) }
  }, [])

  // Map init — (re)build when the date filter (coords) changes. Gated on coords so
  // the #map container exists before Leaflet initialises it.
  useEffect(() => {
    if (!coords || coords.length === 0) return
    if (mapRef.current) { try { mapRef.current.remove() } catch (e) {} mapRef.current = null }
    var map = L.map('map', { center: new L.LatLng(24.4539, 54.3773), attributionControl: false, zoom: 8 })
    mapRef.current = map
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}').addTo(map)
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}').addTo(map)
    const eventsEl = document.querySelector('.events')
    if (eventsEl) { L.DomEvent.disableScrollPropagation(eventsEl); L.DomEvent.disableClickPropagation(eventsEl) }
    const points = coords ? coords.map((p) => [p[1], p[2], p[4]]) : []
    if (points.length > 0) L.heatLayer(points, {radius: 30, blur: 18, maxZoom: 12, minOpacity: 0.4, gradient: {0.15: '#C29717', 0.35: '#e8a520', 0.55: '#e88a2a', 0.75: '#e55', 1.0: '#ff0000'}}).addTo(map)
    if (focusBoundsRef.current) {
      // Center on the selected countries (from the header) whenever the map is (re)built
      try { map.fitBounds(focusBoundsRef.current, { maxZoom: 6 }) } catch (e) {}
    } else if (locked && points.length > 0) {
      try { map.fitBounds(L.latLngBounds(points.map((p) => [p[0], p[1]])).pad(0.4), { maxZoom: 7 }) } catch (e) {}
    }
    map.on('moveend', () => { const b = map.getBounds(); setViewBounds(b); fetchEventsRef.current(b.getNorthWest(), b.getSouthEast()) })
    map.on('zoomend', () => { setViewBounds(map.getBounds()); drawEventMarkers() })
    setViewBounds(map.getBounds())
    drawEventMarkers()
    const ib = map.getBounds(); fetchEventsRef.current(ib.getNorthWest(), ib.getSouthEast())
    return () => { try { map.remove() } catch (e) {} mapRef.current = null }
  }, [coords, fetchEventsRef, locked]) // eslint-disable-line

  // Redraw event markers whenever the panel's filtered set changes (viewport / date filter)
  useEffect(() => { drawEventMarkers() }, [filteredEvents]) // eslint-disable-line

  // Render helpers
  const renderSummaryLines = (text) => {
    if (!text) return null
    return text.split('\n').map((line, i) => {
      if (!line.trim()) return null
      const clean = line.replace(/\*\*/g, '').replace(/^#+\s*/, '').replace(/\[\d+\]/g, '').replace(/\[\w+\]/g, '')
      const isBullet = line.trim().startsWith('-')
      const bulletText = isBullet ? clean.replace(/^\s*-\s*/, '') : clean
      if (isBullet) return <div key={i} style={{display:'flex',gap:'8px',marginBottom:'4px',fontSize:'13px',lineHeight:'170%',color:'#ddd'}}><span style={{color:'#C29717',marginTop:'6px',flexShrink:0}}>&#9642;</span><span>{bulletText}</span></div>
      return <p key={i} style={{color:'#ddd',fontSize:'13px',lineHeight:'170%',margin:'0 0 4px'}}>{clean}</p>
    })
  }

  const renderThreatScore = (text) => {
    if (!text) return null
    const match = text.match(/Threat Score[:\s]*(\d+)\s*\/\s*10/i)
    if (!match) return null
    const score = parseInt(match[1])
    const color = score >= 8 ? '#e55' : score >= 5 ? '#e88a2a' : '#C29717'
    return (
      <div style={{display:'flex',alignItems:'center',gap:'10px',marginTop:'14px',paddingTop:'12px',borderTop:'1px solid rgba(255,255,255,0.06)'}}>
        <span style={{width:'28px',height:'28px',borderRadius:'50%',background:color,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:'12px',fontWeight:600}}>{score}</span>
        <span style={{color:'#888',fontSize:'10px',textTransform:'uppercase',letterSpacing:'1px'}}>Threat score</span>
        <span style={{color,fontSize:'16px',fontWeight:500}}>{score}/10</span>
        <span style={{flex:1,height:'3px',background:'rgba(255,255,255,0.06)',borderRadius:'2px',overflow:'hidden'}}><span style={{display:'block',height:'100%',width:(score*10)+'%',background:color,borderRadius:'2px'}}></span></span>
      </div>
    )
  }

  // Search location
  const handleSearchInput = (val) => {
    setSearchQuery(val)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    if (val.trim().length < 2) { setSearchSuggestions([]); setShowSearchResults(false); return }
    const coordMatch = val.match(/^\s*(-?\d+\.?\d*)\s*[,\s/]\s*(-?\d+\.?\d*)\s*$/)
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]), lng = parseFloat(coordMatch[2])
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        setSearchSuggestions([{ lat, lng, display: lat.toFixed(4) + ', ' + lng.toFixed(4), type: 'coords' }])
        setShowSearchResults(true)
        return
      }
    }
    searchTimeout.current = setTimeout(async () => {
      try {
        // Photon (photon.komoot.io): free, keyless, OSM-based geocoder with real production
        // infrastructure -- swapped in for the public Nominatim demo endpoint, which is
        // fair-use/rate-limited (1 req/s) and not meant to be depended on at any real volume.
        const res = await fetch('https://photon.komoot.io/api/?limit=6&q=' + encodeURIComponent(val))
        const data = await res.json()
        setSearchSuggestions((data.features || []).map(f => {
          const p = f.properties || {}
          const [lng, lat] = f.geometry.coordinates
          const display = [p.name, p.state, p.country].filter(Boolean).join(', ')
          return { lat, lng, display, type: p.osm_value || p.type || 'place', bbox: null }
        }))
        setShowSearchResults(true)
      } catch(e) { setSearchSuggestions([]) }
    }, 350)
  }

  const selectSearchResult = (result) => {
    if (mapRef.current) {
      if (result.bbox) {
        mapRef.current.fitBounds([[parseFloat(result.bbox[0]), parseFloat(result.bbox[2])], [parseFloat(result.bbox[1]), parseFloat(result.bbox[3])]], { animate: true, padding: [20, 20] })
      } else {
        mapRef.current.setView([result.lat, result.lng], 10, { animate: true })
      }
      const marker = L.circleMarker([result.lat, result.lng], { radius: 10, color: '#C29717', weight: 3, fillColor: '#C29717', fillOpacity: 0.5 }).addTo(mapRef.current)
      setTimeout(() => { try { mapRef.current.removeLayer(marker) } catch(e){} }, 15000)
    }
    setSearchQuery('')
    setShowSearchResults(false)
    setSearchSuggestions([])
    setShowSearchBar(false)
    // Notify header to add the country from the search result
    if (result.display) {
      const parts = result.display.split(',').map(s => s.trim())
      const country = parts[parts.length - 1]
      if (country && country.length > 1) {
        window.dispatchEvent(new CustomEvent('forte-add-country', { detail: { country } }))
      }
    }
  }

  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchResults(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div className='map-div'>
      <div>
        <div className='map-toolbar'>
          <img src='icons/logo.webp' alt='' className='logo' />
          <div className='map-actions-div'>
            <div className='map-actions' style={{display:'flex',alignItems:'center',gap:'8px'}}>
              {showSearchBar && (
                <div ref={searchRef} style={{position:'relative',width:'280px',marginRight:'7px',marginTop:'3px'}}>
                  <input type="text" placeholder="Search location or coordinates..." value={searchQuery} autoFocus
                    onChange={e => handleSearchInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && searchSuggestions.length > 0) selectSearchResult(searchSuggestions[0]); if (e.key === 'Escape') { setShowSearchBar(false); setSearchQuery(''); setSearchSuggestions([]); } }}
                    style={{width:'100%',background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',padding:'5px 10px 5px 26px',fontSize:'11px',color:'#fff',outline:'none',fontFamily:'Inter,sans-serif'}} />
                  <svg style={{position:'absolute',left:'7px',top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}} width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='#555' strokeWidth='2'><circle cx='10' cy='10' r='7'/><line x1='15' y1='15' x2='21' y2='21'/></svg>
                  {showSearchResults && searchSuggestions.length > 0 && (
                    <div style={{position:'absolute',top:'100%',left:0,right:0,background:'#1a1a1a',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'0 0 3px 3px',zIndex:99999,maxHeight:'280px',overflowY:'auto',marginTop:'1px',boxShadow:'0 8px 24px rgba(0,0,0,0.5)'}}>
                      {searchSuggestions.map((s, i) => (
                        <div key={i} onClick={() => selectSearchResult(s)}
                          style={{padding:'8px 8px',fontSize:'11px',color:'#ccc',cursor:'pointer',borderBottom:'1px solid rgba(255,255,255,0.04)',textAlign:'left'}}
                          onMouseEnter={e => e.currentTarget.style.background='rgba(194,151,23,0.1)'}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                          {s.display}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <svg onClick={() => { setShowSearchBar(!showSearchBar); if (showSearchBar) { setSearchQuery(''); setSearchSuggestions([]); } }} style={{cursor:'pointer',opacity:0.6,marginRight:'4px'}} width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='1.5'><circle cx='10' cy='10' r='7'/><line x1='15' y1='15' x2='21' y2='21'/></svg>
              <img src='icons/filters.png' alt='' onClick={() => setShowEvents(!showEvents)} className='map-action-img' />
              <img src='icons/graph.png' alt='' className='map-action-img' onClick={() => setShowGraphPanel(true)} />
              <img src='icons/alerts.png' alt='' className='alerts' onClick={() => { setShowAlertsPanel(true); fetchAlertContacts() }} style={{cursor:'pointer'}} />
              <span className='map-action-intel' onClick={fetchIntel} title='Intelligence Assessment'>&#9650;</span>
            </div>
          </div>
        </div>
        {coords && coords.length ? (
          <div className='map-container'>
            {events && showEvents ? (
              <div className='events-container'>
                <div className='events'>
                  <div className='events-header'>
                    <div className='text'><span>Major events</span><div className='circle'>{filteredEvents.length}</div></div>
                    <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                      {dateRange[0].startDate && <span style={{fontSize:'9px',color:'#888',whiteSpace:'nowrap'}}>{dateRange[0].startDate.toLocaleDateString('en-GB',{day:'numeric',month:'short'})} - {dateRange[0].endDate.toLocaleDateString('en-GB',{day:'numeric',month:'short'})}</span>}
                    </div>
                  </div>
                  <hr className='header-hr' />
                  {filteredEvents.map((event, idx) => (
                    <div className='event' key={idx}>
                      <div className='event-title'>
                        <div className='event-location'><img src='icons/pin.png' alt='' className='event-pin' /><span>{event.country}</span></div>
                        <div className='event-date'><span>{moment(new Date(event.timestamp * 1000)).format('D MMM YYYY')}</span></div>
                      </div>
                      <div className='event-subtitle'><span>{formatCoordinates(event.latitude, event.longitude)}</span><span>{moment(new Date(event.timestamp * 1000)).startOf('day').fromNow()}</span></div>
                      <p className='event-desc' style={{fontSize:'12px',fontWeight:'bold'}}>{event.notes}</p>
                      <div className='event-imp'><div className='event-circle-red'></div><span>Critical</span></div>
                      {event.technicalReportUrl && <a href={event.technicalReportUrl} target="_blank" rel="noopener noreferrer" style={{display:'inline-flex',alignItems:'center',gap:'4px',fontSize:'9px',color:'#C29717',textDecoration:'none',marginTop:'4px',border:'1px solid rgba(194,151,23,0.25)',padding:'2px 6px',borderRadius:'2px'}}><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#C29717" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>Technical report</a>}
                      {idx !== filteredEvents.length - 1 && <hr className='event-hr' />}
                    </div>
                  ))}
                </div>
                {showDatePicker && (
                  <div className='date-picker-popup' ref={popupRef}>
                    <DateRangePicker ranges={dateRange} onChange={(ranges) => setDateRange([ranges.selection])} showDateDisplay={false} rangeColors={['black']} />
                    <button className='clear-filter' onClick={() => { setDateRange([{ startDate: new Date(Date.now() - 30*86400000), endDate: new Date(), key: 'selection' }]); setShowDatePicker(false) }}>Reset 30d</button>
                  </div>
                )}
              </div>
            ) : null}
            <div id='map' style={{ height: '95vh', width: '100vw', position: 'relative', zIndex: 4 }}></div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '95vh' }}>
            <div className='forte-loader'><div className='forte-spin'></div></div>
          </div>
        )}
      </div>

      {/* ===== ALERTS PANEL ===== */}
      {showAlertsPanel && (
        <div className='map-panel-overlay' onClick={() => setShowAlertsPanel(false)}>
          <div className='map-panel' onClick={e => e.stopPropagation()} onWheel={e => e.stopPropagation()}>
            <button className='close-modal' onClick={() => setShowAlertsPanel(false)}>&times;</button>
            <div style={{padding:'20px',textAlign:'left'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
                <span style={{color:'#C29717',fontSize:'10px',textTransform:'uppercase',letterSpacing:'2px'}}>Alerting protocols</span>
                <span onClick={() => { setShowAlertForm(!showAlertForm); setEditAlertId(null); setAlertForm({firstName:'',lastName:'',phone:'',telegramId:'',channels:{sms:true,telegram:true},locations:[]}) }} style={{color:'#aaa',fontSize:'12px',cursor:'pointer'}}>{showAlertForm ? 'Cancel' : '+ Add contact'}</span>
              </div>
              {showAlertForm && (
                <div style={{border:'1px solid rgba(255,255,255,0.06)',borderRadius:'2px',padding:'12px',marginBottom:'16px'}}>
                  <div style={{display:'flex',gap:'8px',marginBottom:'8px'}}>
                    <input placeholder="First name *" value={alertForm.firstName} onChange={e => setAlertForm({...alertForm, firstName: e.target.value})} style={{flex:1,background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'2px',padding:'6px 8px',fontSize:'12px',color:'#fff',outline:'none'}} />
                    <input placeholder="Last name *" value={alertForm.lastName} onChange={e => setAlertForm({...alertForm, lastName: e.target.value})} style={{flex:1,background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'2px',padding:'6px 8px',fontSize:'12px',color:'#fff',outline:'none'}} />
                  </div>
                  <div style={{display:'flex',gap:'8px',marginBottom:'8px'}}>
                    <input placeholder="Phone number" value={alertForm.phone} onChange={e => setAlertForm({...alertForm, phone: e.target.value})} style={{flex:1,background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'2px',padding:'6px 8px',fontSize:'12px',color:'#fff',outline:'none'}} />
                    <input placeholder="Telegram ID" value={alertForm.telegramId} onChange={e => setAlertForm({...alertForm, telegramId: e.target.value})} style={{flex:1,background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'2px',padding:'6px 8px',fontSize:'12px',color:'#fff',outline:'none'}} />
                  </div>
                  <div style={{display:'flex',gap:'12px',marginBottom:'10px'}}>
                    {['sms','telegram'].map(ch => (
                      <span key={ch} onClick={() => setAlertForm({...alertForm, channels: {...alertForm.channels, [ch]: !alertForm.channels[ch]}})} style={{display:'flex',alignItems:'center',gap:'4px',cursor:'pointer',fontSize:'11px',color:alertForm.channels[ch]?'#4CAF50':'#888'}}>
                        <span style={{width:'14px',height:'14px',border:'1px solid '+(alertForm.channels[ch]?'#4CAF50':'rgba(255,255,255,0.2)'),borderRadius:'2px',display:'flex',alignItems:'center',justifyContent:'center',background:alertForm.channels[ch]?'rgba(76,175,80,0.15)':'transparent',fontSize:'9px'}}>{alertForm.channels[ch] ? '\u2713' : ''}</span>
                        {ch.toUpperCase()}
                      </span>
                    ))}
                  </div>
                  <div style={{marginBottom:'10px'}}>
                    <span style={{color:'#888',fontSize:'10px',textTransform:'uppercase',letterSpacing:'1px',display:'block',marginBottom:'4px'}}>Coverage areas</span>
                    <div style={{display:'flex',gap:'4px',flexWrap:'wrap',marginBottom:'6px'}}>
                      {alertForm.locations.map((loc, i) => (
                        <span key={i} style={{background:'rgba(194,151,23,0.15)',border:'1px solid rgba(194,151,23,0.3)',borderRadius:'2px',padding:'2px 8px',fontSize:'10px',color:'#C29717',display:'flex',alignItems:'center',gap:'4px'}}>
                          {loc}<span style={{cursor:'pointer',fontSize:'12px'}} onClick={() => setAlertForm({...alertForm, locations: alertForm.locations.filter((_,idx) => idx !== i)})}>&times;</span>
                        </span>
                      ))}
                    </div>
                    <div style={{position:'relative'}}>
                      <input placeholder="Add country..." value={alertLocInput} onChange={e => setAlertLocInput(e.target.value)} style={{width:'100%',background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'2px',padding:'6px 8px',fontSize:'11px',color:'#fff',outline:'none'}} />
                      {alertLocInput.length >= 2 && (
                        <div style={{position:'absolute',top:'100%',left:0,right:0,background:'#252525',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'2px',zIndex:9999,maxHeight:'150px',overflowY:'auto',marginTop:'2px'}}>
                          {COUNTRIES.filter(co => co.toLowerCase().includes(alertLocInput.toLowerCase()) && !alertForm.locations.includes(co)).map(co => (
                            <div key={co} onClick={() => { setAlertForm({...alertForm, locations: [...alertForm.locations, co]}); setAlertLocInput('') }} style={{padding:'6px 8px',fontSize:'11px',color:'#ccc',cursor:'pointer'}} onMouseEnter={e => e.target.style.background='rgba(194,151,23,0.1)'} onMouseLeave={e => e.target.style.background='transparent'}>{co}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <span onClick={saveAlert} style={{padding:'6px 16px',background:'rgba(194,151,23,0.15)',border:'1px solid rgba(194,151,23,0.3)',borderRadius:'2px',color:'#C29717',fontSize:'11px',cursor:'pointer'}}>{editAlertId ? 'Update' : 'Save contact'}</span>
                </div>
              )}
              {alertContacts.map(item => (
                <div key={item._id} style={{borderBottom:'1px solid rgba(255,255,255,0.04)',padding:'10px 0',display:'flex',justifyContent:'space-between',alignItems:'flex-start',opacity:item.active?1:0.4}}>
                  <div>
                    <div style={{color:'#fff',fontSize:'13px',fontWeight:500,marginBottom:'3px'}}>{item.firstName} {item.lastName}</div>
                    <div style={{display:'flex',gap:'8px',marginBottom:'3px',fontSize:'10px',color:'#888'}}>
                      {item.phone && <span>Tel: {item.phone}</span>}
                      {item.telegramId && <span>TG: {item.telegramId}</span>}
                    </div>
                    <div style={{display:'flex',gap:'3px',flexWrap:'wrap'}}>
                      {(item.locations||[]).map((loc,i) => <span key={i} style={{fontSize:'9px',color:'#C29717',background:'rgba(194,151,23,0.08)',borderRadius:'2px',padding:'0 4px'}}>{loc}</span>)}
                    </div>
                  </div>
                  <div style={{display:'flex',gap:'6px',alignItems:'center',flexShrink:0}}>
                    <span onClick={() => toggleAlertActive(item)} style={{width:'12px',height:'12px',borderRadius:'2px',border:'1px solid '+(item.active?'#4CAF50':'rgba(255,255,255,0.15)'),background:item.active?'rgba(76,175,80,0.15)':'transparent',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',fontSize:'8px',color:'#4CAF50'}}>{item.active?'\u2713':''}</span>
                    <span onClick={() => { setEditAlertId(item._id); setAlertForm({firstName:item.firstName,lastName:item.lastName,phone:item.phone||'',telegramId:item.telegramId||'',channels:item.channels||{sms:true,telegram:true},locations:item.locations||[]}); setShowAlertForm(true) }} style={{color:'#888',cursor:'pointer',fontSize:'12px'}}>{'\u270E'}</span>
                    <span onClick={() => deleteAlert(item._id)} style={{color:'#888',cursor:'pointer',fontSize:'12px'}}>x</span>
                  </div>
                </div>
              ))}
              {alertContacts.length === 0 && <p style={{color:'#888',fontSize:'12px',textAlign:'center',padding:'20px 0'}}>No alert contacts configured</p>}
            </div>
          </div>
        </div>
      )}

      {/* ===== INTEL PANEL ===== */}
      {showIntelPanel && (
        <div className='map-panel-overlay' onClick={() => setShowIntelPanel(false)}>
          <div className='map-panel' onClick={e => e.stopPropagation()} onWheel={e => e.stopPropagation()}>
            <button className='close-modal' onClick={() => setShowIntelPanel(false)}>&times;</button>
            <div style={{display:'flex',gap:0,borderBottom:'1px solid rgba(255,255,255,0.06)',padding:'12px 20px 0'}}>
              {['intel','arsenal','reports'].map(t => (
                <div key={t} onClick={() => setIntelTab(t)} style={{padding:'6px 14px',cursor:'pointer',fontSize:'11px',textTransform:'uppercase',letterSpacing:'1.5px',color:intelTab===t?'#fff':'#666',borderBottom:intelTab===t?'1px solid #C29717':'1px solid transparent',marginBottom:'-1px'}}>{t === 'intel' ? 'Intel' : t === 'arsenal' ? 'Arsenal' : 'Reports (' + mapReports.length + ')'}</div>
              ))}
            </div>
            <div style={{padding:'20px',textAlign:'left'}}>
              {intelTab === 'intel' && (
                <div>
                  {intelLoading ? (
                    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'200px'}}><div className='forte-spin' style={{width:'40px',height:'40px'}}></div></div>
                  ) : intelData ? (
                    <div>
                      {intelData.sitrep && (
                        <div style={{marginBottom:'24px'}}>
                          <div style={{color:'#C29717',fontSize:'10px',textTransform:'uppercase',letterSpacing:'2px',marginBottom:'12px',paddingBottom:'6px',borderBottom:'1px solid rgba(194,151,23,0.2)'}}>Sitrep</div>
                          {renderSummaryLines(intelData.sitrep)}
                        </div>
                      )}
                      {intelData.forecast && (
                        <div style={{marginBottom:'24px'}}>
                          <div style={{color:'#C29717',fontSize:'10px',textTransform:'uppercase',letterSpacing:'2px',marginBottom:'12px',paddingBottom:'6px',borderBottom:'1px solid rgba(194,151,23,0.2)'}}>Forecast</div>
                          {renderSummaryLines(intelData.forecast)}
                          {renderThreatScore(intelData.forecast)}
                        </div>
                      )}
                      {intelData.threats && intelData.threats.length > 0 && (
                        <div>
                          <div style={{color:'#C29717',fontSize:'10px',textTransform:'uppercase',letterSpacing:'2px',marginBottom:'12px',paddingBottom:'6px',borderBottom:'1px solid rgba(194,151,23,0.2)'}}>Potential threat zones</div>
                          {intelData.threats.map((t, i) => {
                            const rc = t.risk === 'high' ? '#e55' : t.risk === 'medium' ? '#e88a2a' : '#C29717'
                            return (
                              <div key={i} style={{display:'flex',gap:'10px',alignItems:'flex-start',padding:'8px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                                <span style={{width:'8px',height:'8px',borderRadius:'50%',background:rc,marginTop:'4px',flexShrink:0}}></span>
                                <div>
                                  <div style={{color:'#fff',fontSize:'13px',fontWeight:500,marginBottom:'2px'}}>{t.target}</div>
                                  <div style={{color:'#999',fontSize:'11px'}}>{t.type}, {t.risk} risk</div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  ) : <p style={{color:'#888'}}>No assessment available</p>}
                </div>
              )}
              {intelTab === 'arsenal' && (
                <div>
                  {mapArsenal && mapArsenal.weapons && mapArsenal.weapons.length > 0 ? (
                    <div>
                      {['offensive', 'defensive'].map(role => {
                        const items = (mapArsenal.weapons || []).filter(w => w.role === role)
                        if (items.length === 0) return null
                        return (
                          <div key={role} style={{marginBottom: '16px'}}>
                            <div style={{color: '#C29717', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px', paddingBottom: '4px', borderBottom: '1px solid rgba(194,151,23,0.15)'}}>{role} systems</div>
                            {items.map((w, i) => (
                              <div key={i} style={{display: 'flex', gap: '10px', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', alignItems: 'flex-start'}}>
                                {w.image && <img src={window.location.origin + w.image} alt="" style={{width: '55px', height: '35px', objectFit: 'cover', borderRadius: '2px', border: '1px solid rgba(255,255,255,0.08)'}} />}
                                <div>
                                  <div style={{color: '#fff', fontSize: '12px', fontWeight: 500}}>{w.name} <span style={{color: '#C29717', fontSize: '9px', marginLeft: '4px'}}>{w.designation || ''}</span></div>
                                  <div style={{color: '#888', fontSize: '10px'}}>{w.manufacturer} &mdash; {w.operator} &mdash; {w.range_km}km</div>
                                  <div style={{color: '#C29717', fontSize: '9px'}}>x{w.frequency}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <p style={{color: '#888', fontSize: '12px'}}>Loading arsenal...</p>
                  )}
                </div>
              )}
              {intelTab === 'reports' && (
                <div>
                  {mapReports.length === 0 ? <p style={{color:'#888'}}>No reports available</p> : mapReports.map((r, idx) => {
                    const isExp = expReport === r._id || (expReport === 'first' && idx === 0)
                    return (
                      <div key={r._id} style={{borderBottom:'1px solid rgba(255,255,255,0.04)',marginBottom:'4px'}}>
                        <div onClick={() => setExpReport(isExp ? null : r._id)} style={{cursor:'pointer',padding:'10px 0',display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                          <div>
                            <div style={{color:'#C29717',fontSize:'10px',letterSpacing:'1px',marginBottom:'3px'}}>{r.date ? new Date(r.date).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : ''}</div>
                            <div style={{display:'flex',gap:'4px',marginBottom:'4px'}}>{(r.categories||['Intelligence']).map(cat => <span key={cat} style={{fontSize:'9px',textTransform:'uppercase',letterSpacing:'1px',padding:'2px 7px',borderRadius:'2px',background:'rgba(194,151,23,0.12)',border:'1px solid rgba(194,151,23,0.25)',color:'#C29717'}}>{cat} report</span>)}</div>
                            <div style={{color:'#fff',fontSize:'13px',fontWeight:500}}>{r.title}</div>
                          </div>
                          <div style={{display:'flex',gap:'8px',alignItems:'center',flexShrink:0}}>
                            <span style={{color:'#888',fontSize:'12px',cursor:'pointer'}} onClick={e => { e.stopPropagation(); window.open(API + '/report-view/' + r._id, '_blank') }}>&#8681;</span>
                            <span style={{color:isExp?'#C29717':'#666',fontSize:'14px',transition:'0.2s',transform:isExp?'rotate(180deg)':'none'}}>&#9662;</span>
                          </div>
                        </div>
                        {isExp && (
                          <div style={{paddingBottom:'12px'}}>
                            <div dangerouslySetInnerHTML={{__html: formatContent(r.content)}} style={{color:'#ddd',fontSize:'13px',lineHeight:'170%'}} />
                            {r.images && r.images.length > 0 && (
                              <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginTop:'10px'}}>
                                {r.images.map((img,i) => (
                                  <div key={i} style={{width:'120px',borderRadius:'2px',overflow:'hidden',border:'1px solid rgba(255,255,255,0.08)',cursor:'pointer'}} onClick={() => setLightbox({open:true,images:r.images,index:i})}>
                                    <img src={window.location.origin+img.url} alt="" style={{width:'100%',height:'70px',objectFit:'cover'}} />
                                    {img.label && <div style={{fontSize:'9px',color:'#C29717',padding:'3px 5px',background:'rgba(0,0,0,0.3)'}}>{img.label}</div>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== LIGHTBOX ===== */}
      {lightbox.open && (
        <div className='map-panel-overlay' style={{zIndex:3000,justifyContent:'center',alignItems:'center'}} onClick={() => setLightbox({open:false,images:[],index:0})}>
          <div style={{position:'relative',maxWidth:'90vw',maxHeight:'90vh',display:'flex',flexDirection:'column',alignItems:'center'}} onClick={e => e.stopPropagation()}>
            <button className='close-modal' style={{position:'absolute',top:'-35px',right:'-10px'}} onClick={() => setLightbox({open:false,images:[],index:0})}>&times;</button>
            {lightbox.images.length > 1 && <div style={{position:'absolute',top:'50%',left:'-50px',transform:'translateY(-50%)',background:'rgba(255,255,255,0.08)',width:'36px',height:'36px',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:'#fff',fontSize:'28px'}} onClick={() => setLightbox(p => ({...p,index:(p.index-1+p.images.length)%p.images.length}))}>&lsaquo;</div>}
            <img src={window.location.origin+(lightbox.images[lightbox.index]?.url||'')} alt="" style={{maxWidth:'85vw',maxHeight:'78vh',objectFit:'contain',borderRadius:'4px'}} />
            {lightbox.images.length > 1 && <div style={{position:'absolute',top:'50%',right:'-50px',transform:'translateY(-50%)',background:'rgba(255,255,255,0.08)',width:'36px',height:'36px',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:'#fff',fontSize:'28px'}} onClick={() => setLightbox(p => ({...p,index:(p.index+1)%p.images.length}))}>&rsaquo;</div>}
            <div style={{marginTop:'10px',display:'flex',gap:'12px',alignItems:'center'}}>
              <span style={{color:'#C29717',fontSize:'12px'}}>{lightbox.images[lightbox.index]?.label || ''}</span>
              <span style={{color:'#888',fontSize:'12px'}}>{lightbox.index+1} / {lightbox.images.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* ===== GRAPH PANEL ===== */}
      {showGraphPanel && (
        <div className='map-panel-overlay'>
          <div className='map-panel' onWheel={e => e.stopPropagation()}>
            <button className='close-modal' onClick={() => setShowGraphPanel(false)}>&times;</button>
            <div style={{display:'flex',gap:0,borderBottom:'1px solid rgba(255,255,255,0.06)',padding:'16px 20px 0'}}>
              {['events','casualties','cumulative','timeline'].map(t => (
                <div key={t} onClick={() => setGraphTab(t)} style={{padding:'8px 16px',cursor:'pointer',fontSize:'11px',textTransform:'uppercase',letterSpacing:'1.5px',color:graphTab===t?'#fff':'#666',borderBottom:graphTab===t?'1px solid #C29717':'1px solid transparent',marginBottom:'-1px'}}>{t === 'cumulative' ? 'Threat level' : t}</div>
              ))}
            </div>
            <div style={{padding:'20px'}}>
              {graphTab === 'events' && chartData.length > 0 && (
                <div>
                  <div style={{color:'#C29717',fontSize:'10px',textTransform:'uppercase',letterSpacing:'1.5px',marginBottom:'16px'}}>Daily incidents</div>
                  <ResponsiveContainer width="100%" height={380}>
                    <ComposedChart data={chartData} margin={{top:10,right:10,left:0,bottom:0}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                      <XAxis dataKey="date" tick={{fill:'#888',fontSize:10}} tickFormatter={d => moment(d).format('DD MMM')} />
                      <YAxis yAxisId="left" tick={{fill:'#888',fontSize:10}} />
                      <YAxis yAxisId="right" orientation="right" tick={{fill:'#888',fontSize:10}} />
                      <Tooltip contentStyle={{background:'#1e1e1e',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'2px',fontSize:'11px',color:'#ccc'}} />
                      <Bar yAxisId="left" dataKey="eventsCount" name="Events" fill="rgba(194,151,23,0.6)" radius={[2,2,0,0]} />
                      <Line yAxisId="right" type="monotone" dataKey="fatalities" name="Casualties" stroke="#e55" strokeWidth={2} dot={{r:2,fill:'#e55'}} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
              {graphTab === 'casualties' && chartData.length > 0 && (
                <div>
                  <div style={{color:'#C29717',fontSize:'10px',textTransform:'uppercase',letterSpacing:'1.5px',marginBottom:'16px'}}>Daily casualties</div>
                  <ResponsiveContainer width="100%" height={380}>
                    <BarChart data={chartData} margin={{top:10,right:10,left:0,bottom:0}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                      <XAxis dataKey="date" tick={{fill:'#888',fontSize:10}} tickFormatter={d => moment(d).format('DD MMM')} />
                      <YAxis tick={{fill:'#888',fontSize:10}} />
                      <Tooltip contentStyle={{background:'#1e1e1e',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'2px',fontSize:'11px',color:'#ccc'}} />
                      <Bar dataKey="fatalities" name="Casualties" fill="rgba(220,50,50,0.7)" radius={[2,2,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              {graphTab === 'cumulative' && chartData.length > 0 && (() => {
                let cum = 0
                const cumData = chartData.map(d => { cum += d.fatalities; return {...d, cumCas: cum} })
                return (
                  <div>
                    <div style={{color:'#C29717',fontSize:'10px',textTransform:'uppercase',letterSpacing:'1.5px',marginBottom:'16px'}}>Cumulative threat level</div>
                    <ResponsiveContainer width="100%" height={380}>
                      <AreaChart data={cumData} margin={{top:10,right:10,left:0,bottom:0}}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                        <XAxis dataKey="date" tick={{fill:'#888',fontSize:10}} tickFormatter={d => moment(d).format('DD MMM')} />
                        <YAxis tick={{fill:'#888',fontSize:10}} />
                        <Tooltip contentStyle={{background:'#1e1e1e',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'2px',fontSize:'11px',color:'#ccc'}} />
                        <defs><linearGradient id="tg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#e55" stopOpacity={0.4}/><stop offset="100%" stopColor="#e55" stopOpacity={0.05}/></linearGradient></defs>
                        <Area type="monotone" dataKey="cumCas" name="Cumulative casualties" stroke="#e55" fill="url(#tg)" strokeWidth={1.5} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )
              })()}
              {graphTab === 'timeline' && (
                <div style={{maxHeight:'70vh',overflowY:'auto'}}>
                  <div style={{color:'#C29717',fontSize:'10px',textTransform:'uppercase',letterSpacing:'1.5px',marginBottom:'16px'}}>Event timeline</div>
                  {timelineData.map(event => {
                    const severityColor = event.fatalities > 10 ? '#e55' : event.fatalities > 0 ? '#e88a2a' : '#C29717'
                    return (
                      <div key={event.id} style={{display:'flex',gap:'12px',marginBottom:'12px',paddingBottom:'12px',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                        <div style={{width:'8px',height:'8px',borderRadius:'50%',background:severityColor,marginTop:'4px',flexShrink:0}}></div>
                        <div>
                          <div style={{display:'flex',justifyContent:'space-between',gap:'12px'}}>
                            <span style={{color:'#fff',fontSize:'13px',fontWeight:500}}>{event.title}</span>
                            <span style={{color:'#C29717',fontSize:'10px',whiteSpace:'nowrap'}}>{event.subtitle}</span>
                          </div>
                          {event.fatalities > 0 && <span style={{color:'#e55',fontSize:'11px'}}>{event.fatalities} fatalities</span>}
                          <p style={{color:'#bbb',fontSize:'12px',lineHeight:1.5,margin:'4px 0 0'}}>{event.description}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
